import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Search, Beaker, Mail, GraduationCap } from "lucide-react";
import { useAcademicPIs } from "@/hooks/useAcademicMarket";

interface AccountPIsTabProps {
  accountId: string;
}

type SortField = "full_name" | "department" | "enrichment_status";
type SortDirection = "asc" | "desc";

export function AccountPIsTab({ accountId }: AccountPIsTabProps) {
  const { data: investigators, isLoading } = useAcademicPIs(accountId);
  const [sortField, setSortField] = useState<SortField>("full_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [search, setSearch] = useState("");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (!investigators || investigators.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No principal investigators found</p>
        <p className="text-sm mt-1">PIs are synced from the Life Science Hub</p>
      </div>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filtered = investigators.filter((pi) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      pi.full_name.toLowerCase().includes(q) ||
      (pi.department || "").toLowerCase().includes(q) ||
      (pi.research_areas || []).some((a) => a.toLowerCase().includes(q))
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDirection === "asc" ? 1 : -1;
    switch (sortField) {
      case "full_name":
        return dir * a.full_name.localeCompare(b.full_name);
      case "department":
        return dir * (a.department || "").localeCompare(b.department || "");
      case "enrichment_status":
        return dir * a.enrichment_status.localeCompare(b.enrichment_status);
      default:
        return 0;
    }
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground" />;
    return sortDirection === "asc" ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "lab_discovered": return "default";
      case "enriched": return "default";
      case "pending": return "secondary";
      case "error": return "destructive";
      default: return "outline";
    }
  };

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search PIs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length} of {investigators.length} PIs
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[25%]">
                <Button variant="ghost" onClick={() => handleSort("full_name")} className="h-8 font-medium hover:bg-transparent p-0">
                  Name <SortIcon field="full_name" />
                </Button>
              </TableHead>
              <TableHead className="w-[20%]">
                <Button variant="ghost" onClick={() => handleSort("department")} className="h-8 font-medium hover:bg-transparent p-0">
                  Department <SortIcon field="department" />
                </Button>
              </TableHead>
              <TableHead className="w-[20%]">Research</TableHead>
              <TableHead className="w-[15%]">Lab</TableHead>
              <TableHead className="w-[10%]">
                <Button variant="ghost" onClick={() => handleSort("enrichment_status")} className="h-8 font-medium hover:bg-transparent p-0">
                  Status <SortIcon field="enrichment_status" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No principal investigators found
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((pi) => (
                <TableRow key={pi.id} className="hover:bg-accent/50">
                  <TableCell>
                    <div className="font-medium">{pi.full_name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {pi.email && (
                        <a href={`mailto:${pi.email}`} onClick={(e) => e.stopPropagation()} className="text-primary">
                          <Mail className="h-3 w-3" />
                        </a>
                      )}
                      {pi.google_scholar_url && (
                        <a href={pi.google_scholar_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {pi.department || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(pi.therapeutic_focus || []).slice(0, 2).map((area, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {pi.lab_website_url ? (
                      <a
                        href={pi.lab_website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                      >
                        <Beaker className="h-3 w-3" />
                        Lab
                      </a>
                    ) : pi.lab_name ? (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Beaker className="h-3 w-3" />
                        {pi.lab_name}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusColor(pi.enrichment_status) as any} className="text-xs">
                      {pi.enrichment_status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
